import { Link } from 'react-router-dom'
import { usePageMeta } from '../hooks/usePageMeta'

export default function PrivacyPage() {
  usePageMeta(
    'Privacy Policy',
    'What Biosecurity Reporter collects and how it is used — account details, report submissions, and location data for H5N1 avian influenza contact reporting in Australia.',
    '/privacy',
  )

  return (
    <div className="min-h-screen bg-gray-50 px-4 py-10">
      <div className="mx-auto max-w-2xl">
        <Link to="/" className="text-sm text-blue-600 hover:text-blue-700 font-medium">
          ← Back to map
        </Link>

        <div className="mt-4 mb-8">
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Privacy Policy</h1>
          <p className="mt-1 text-sm text-gray-500">Last updated 15 August 2026</p>
        </div>

        <div className="text-sm text-gray-700 space-y-8">
          <section>
            <h2 className="text-base font-semibold text-gray-900 mb-2">What we collect</h2>
            <ul className="list-disc pl-5 space-y-1.5">
              <li><strong>Account details:</strong> username, email address, and a securely
                hashed password (we never store your password in plain text).</li>
              <li><strong>Report content:</strong> species, event type, an optional description,
                the location and time you specify, and any evidence photos you attach.</li>
              <li><strong>Location data:</strong> if you use &quot;current location&quot; when
                submitting a report, that&apos;s your device&apos;s GPS coordinates at that
                moment — which, especially for a report made from home, can effectively reveal
                where you live. You can enter coordinates manually instead if you&apos;d rather
                not share your device&apos;s exact location.</li>
              <li><strong>Professional-tier requests:</strong> if you request verified-professional
                status, whatever details you provide to support that request.</li>
              <li><strong>Standard technical data:</strong> IP address and request metadata,
                collected automatically for rate limiting and abuse prevention.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-base font-semibold text-gray-900 mb-2">How we use it</h2>
            <p>
              To operate the platform: publishing and fusing reports into contacts, computing
              confidence scores, assessing credibility tiers, moderating flagged reports, securing
              your account, and — if you request a password reset — emailing you a reset link.
              We don&apos;t use your data for advertising, and we don&apos;t sell it.
            </p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-gray-900 mb-2">What&apos;s public, and what isn&apos;t</h2>
            <p>
              Contacts shown on the public map display aggregate information only — species,
              confidence status, report count, and a reporter-tier breakdown like &quot;4 reports,
              1 verified professional&quot;. Your individual identity is never shown alongside a
              report, publicly or to other users. Administrators can see individual reports
              (including who submitted them) for moderation purposes.
            </p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-gray-900 mb-2">Official detections</h2>
            <p>
              Some contacts on the map originate from official government sources (via CAP-alert-
              platform&apos;s public alerts feed) rather than a user report. This is one-directional
              — we read publicly published official alerts to show alongside crowd reports; we
              don&apos;t send your data to that or any other government platform.
            </p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-gray-900 mb-2">Third-party services</h2>
            <p className="mb-2">A few external services support the platform's operation:</p>
            <ul className="list-disc pl-5 space-y-1.5">
              <li><strong>Resend</strong> — sends password-reset emails, so receives your email
                address when you request one.</li>
              <li><strong>MapTiler</strong> — supplies the map imagery; loading map tiles sends
                your IP address to MapTiler as part of standard web requests, per their own
                privacy policy.</li>
            </ul>
            <p className="mt-2">
              Evidence photos are stored on infrastructure we operate directly, not a third-party
              cloud storage provider, and are only ever accessible to you and admins.
            </p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-gray-900 mb-2">Data retention</h2>
            <p>
              We keep report and account data for as long as your account is active, or as long
              as needed for the platform&apos;s biosecurity-awareness purpose (including historical
              trend value), whichever is longer. You can request deletion at any time — see
              below.
            </p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-gray-900 mb-2">Security</h2>
            <p>
              Passwords are hashed with bcrypt, never stored or logged in plain text. All traffic
              to the platform is encrypted (HTTPS). Password-reset tokens are single-use, expire
              after 30 minutes, and only their hash is ever stored.
            </p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-gray-900 mb-2">Your rights</h2>
            <p>
              You can request access to, correction of, or deletion of your personal information
              at any time by emailing us. We&apos;ll respond within a reasonable time. Deleting
              your account removes your personal identifying information; reports you&apos;ve
              contributed may be retained in de-identified, aggregate form as part of the
              historical record other users already see on the map.
            </p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-gray-900 mb-2">Data breaches</h2>
            <p>
              If a data breach occurs that&apos;s likely to result in serious harm, we&apos;ll
              notify affected users and the Office of the Australian Information Commissioner in
              line with the Notifiable Data Breaches scheme.
            </p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-gray-900 mb-2">Changes to this policy</h2>
            <p>
              We may update this policy from time to time; material changes will be reflected in
              the &quot;last updated&quot; date above.
            </p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-gray-900 mb-2">Contact</h2>
            <p>
              Privacy questions or requests:{' '}
              <a href="mailto:robrussell.development@gmail.com" className="text-blue-600 hover:text-blue-700 font-medium">
                robrussell.development@gmail.com
              </a>
            </p>
          </section>
        </div>

        <div className="mt-10">
          <Link to="/" className="text-sm text-blue-600 hover:text-blue-700 font-medium">
            ← Back to map
          </Link>
        </div>
      </div>
    </div>
  )
}
